import type { StudentDetail as Detail } from "../lib/types";
import { Card, Field, HoldTag, RiskBadge } from "./ui";

const STATUS_STYLES: Record<string, string> = {
  passed: "text-emerald-700",
  failed: "text-rose-700",
  enrolled: "text-slate-500",
  withdrawn: "text-slate-400 line-through",
};

export default function StudentDetail({ detail }: { detail: Detail }) {
  const s = detail.student;
  if (!s) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
        No student record is linked to this account.
      </div>
    );
  }

  const requirements = detail.progression.requirements;
  const risks = detail.progression.risks;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {s.preferred_name} {s.family_name}
            </h2>
            <p className="text-sm text-slate-500">
              {s.student_id} · {s.program_title ?? s.program_code}
              {s.major ? ` · ${s.major}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <RiskBadge
              severity={risks.length ? risks[0].severity : "none"}
              count={risks.length || undefined}
            />
            {(s.holds ?? []).map((h) => (
              <HoldTag key={h} code={h} />
            ))}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Field label="WAM" value={s.wam} />
          <Field label="Credit points" value={s.credit_points_completed} />
          <Field label="Load" value={s.load_status} />
          <Field label="Citizenship" value={s.citizenship} />
          <Field label="Commenced" value={s.commenced} />
          <Field
            label="Fee balance"
            value={
              s.fee_balance_aud
                ? `A$${s.fee_balance_aud.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                : "Nil"
            }
          />
        </div>
      </div>

      {/* Progression risks */}
      <Card title="Progression">
        {requirements.credit_points_required != null && (
          <p className="mb-3 text-sm text-slate-600">
            {requirements.credit_points_completed} of {requirements.credit_points_required}{" "}
            credit points complete
            {requirements.credit_points_short
              ? ` · ${requirements.credit_points_short} short`
              : ""}
            {requirements.outstanding_core_units?.length
              ? ` · core outstanding: ${requirements.outstanding_core_units.join(", ")}`
              : ""}
            .
          </p>
        )}
        {risks.length === 0 ? (
          <p className="text-sm text-emerald-700">No progression risks flagged.</p>
        ) : (
          <ul className="space-y-2">
            {risks.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <RiskBadge severity={r.severity} />
                <span className="text-slate-700">{r.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Enrolments */}
      <Card title="Enrolment history">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="pb-1">Unit</th>
              <th className="pb-1">Term</th>
              <th className="pb-1">Status</th>
              <th className="pb-1 text-right">Mark</th>
              <th className="pb-1 text-right">Grade</th>
            </tr>
          </thead>
          <tbody>
            {detail.enrolments.map((e, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="py-1 font-medium text-slate-800">{e.unit_code}</td>
                <td className="py-1 text-slate-500">{e.term}</td>
                <td className={`py-1 ${STATUS_STYLES[e.status] ?? "text-slate-600"}`}>
                  {e.status}
                </td>
                <td className="py-1 text-right text-slate-700">{e.mark ?? "—"}</td>
                <td className="py-1 text-right text-slate-700">{e.grade ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Advising notes */}
      <Card title="Advising notes">
        {detail.advising_notes.length === 0 ? (
          <p className="text-sm text-slate-500">No advising notes.</p>
        ) : (
          <ul className="space-y-3">
            {detail.advising_notes.map((n) => (
              <li key={n.note_id} className="rounded-md border border-slate-100 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-800">{n.subject}</span>
                  <span
                    className={`rounded-full border px-2 py-0.5 text-xs font-medium ${
                      n.visibility === "staff"
                        ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                        : "border-slate-200 bg-slate-50 text-slate-500"
                    }`}
                  >
                    {n.visibility === "staff" ? "Staff only" : "Student visible"}
                  </span>
                </div>
                <p className="note-body mt-1 text-sm text-slate-600">{n.body}</p>
                <p className="mt-1 text-xs text-slate-400">
                  {n.authored_by} · {n.created}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Fees */}
      {detail.fees.length > 0 && (
        <Card title="Fees">
          <ul className="space-y-1 text-sm">
            {detail.fees.map((f, i) => (
              <li key={i} className="flex items-center justify-between">
                <span className="text-slate-700">
                  {f.description} <span className="text-slate-400">({f.term})</span>
                </span>
                <span
                  className={
                    f.status === "overdue" ? "font-medium text-rose-700" : "text-slate-500"
                  }
                >
                  A${f.amount_aud.toLocaleString(undefined, { minimumFractionDigits: 2 })} ·{" "}
                  {f.status}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
